import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  ConversationDetail,
  ConversationMessage,
  ConversationSummary,
  SendMessageResponse,
  ToolInvocation,
} from "@/types";

interface ConversationListResponse {
  conversations: ConversationSummary[];
}

const conversationsKey = ["personal-agent", "conversations"] as const;
const conversationKey = (id: string) => ["personal-agent", "conversation", id] as const;

export function useConversations() {
  return useQuery<ConversationSummary[]>({
    queryKey: conversationsKey,
    queryFn: async () => {
      const res = await apiClient.get<ConversationListResponse>("/personal-agent/conversations");
      return res.data.conversations;
    },
  });
}

export function useConversation(id: string | undefined) {
  return useQuery<ConversationDetail>({
    queryKey: id ? conversationKey(id) : ["personal-agent", "conversation", "none"],
    queryFn: async () => {
      const res = await apiClient.get<ConversationDetail>(`/personal-agent/conversations/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params?: { title?: string }) => {
      const res = await apiClient.post<ConversationSummary>("/personal-agent/conversations", {
        title: params?.title ?? null,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: conversationsKey }),
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/personal-agent/conversations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: conversationsKey }),
  });
}

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const res = await apiClient.patch<ConversationSummary>(
        `/personal-agent/conversations/${id}`,
        { title },
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: conversationsKey });
      qc.invalidateQueries({ queryKey: conversationKey(vars.id) });
    },
  });
}

export function useSendMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      if (!conversationId) throw new Error("No conversation selected");
      const res = await apiClient.post<SendMessageResponse>(
        `/personal-agent/conversations/${conversationId}/messages`,
        { content },
      );
      return res.data;
    },
    onMutate: async (content) => {
      if (!conversationId) return { previous: undefined };
      const key = conversationKey(conversationId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ConversationDetail>(key);
      if (previous) {
        const optimistic: ConversationMessage = {
          id: `pending-${Date.now()}`,
          role: "user",
          content,
          tool_name: null,
          tool_arguments: null,
          tool_invocation_id: null,
          tool_invocation_status: null,
          tool_result: null,
          created_at: new Date().toISOString(),
        };
        qc.setQueryData<ConversationDetail>(key, {
          ...previous,
          messages: [...previous.messages, optimistic],
        });
      }
      return { previous };
    },
    onError: (_err, _content, ctx) => {
      if (ctx?.previous && conversationId) {
        qc.setQueryData(conversationKey(conversationId), ctx.previous);
      }
    },
    onSettled: () => {
      if (!conversationId) return;
      qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      qc.invalidateQueries({ queryKey: conversationsKey });
    },
  });
}

export function useConfirmInvocation(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      invocationId,
      params,
    }: {
      invocationId: string;
      params: Record<string, unknown>;
    }) => {
      const res = await apiClient.post<ToolInvocation>(
        `/personal-agent/tool-invocations/${invocationId}/confirm`,
        { params },
      );
      return res.data;
    },
    onSuccess: () => {
      if (conversationId) {
        qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      }
    },
  });
}

export function useRejectInvocation(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invocationId: string) => {
      const res = await apiClient.post<ToolInvocation>(
        `/personal-agent/tool-invocations/${invocationId}/reject`,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      if (conversationId) {
        qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      }
    },
  });
}
